"use client";

import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  FileQuestion,
  Pencil,
} from "lucide-react";
import PostCard from "@/app/community/restoration/PostCard";
import ProblemMetaAdminPanel from "@/app/community/restoration/ProblemMetaAdminPanel";
import { sortRestorationPosts } from "@/lib/restorationSort";
import type { RestorationExamSession, RestorationPost, RestorationProblemMeta } from "@/lib/server-actions/restoration/_shared";

interface ProblemAccordionProps {
  meta: RestorationProblemMeta;
  posts: RestorationPost[];
  myVotes: Record<number, "up" | "down">;
  currentUserId: string | null;
  isAdmin: boolean;
  sessions: RestorationExamSession[];
  onPostChanged: (
    postId: number,
    next: Partial<RestorationPost> | null
  ) => void;
  onRefreshNeeded: () => void;
  /** 스크롤 타깃용 앵커 id (셸 좌측 문제 네비 클릭 이동, JCG-2102) */
  anchorId?: string;
  /** 외부 제어 펼침 상태 — 지정 시 controlled, 미지정 시 내부 useState(기존 동작) */
  open?: boolean;
  /** controlled 모드일 때 토글 콜백 */
  onToggle?: () => void;
  /** uncontrolled 모드의 초기 펼침 여부 (댓글 딥링크 `?post=` 진입, JCG-3603) */
  defaultOpen?: boolean;
  /** 이 게시물 id 의 카드를 펼친 채 시작한다 (댓글 딥링크 `?post=` 진입, JCG-3603) */
  expandedPostId?: number | null;
}

/**
 * JCG-1569: 1~20번 문제 아코디언 항목.
 * JCG-1572: 관리자 메타 편집 (카테고리/요약) 인라인 토글 추가.
 *
 * - 접힘: 번호 + 카테고리 배지 + 요약 + 복원 수 + 최고 투표수
 * - 펼침: (관리자 only) 메타 편집 버튼/패널 + 정렬된 게시물 목록
 *   - 확정 게시물은 최상단 고정
 *   - 1위(첫 번째 게시물)는 `alwaysExpanded`로 ProblemAnswer 즉시 렌더
 *   - 나머지는 접힘 상태, 사용자가 "펼치기" 클릭 시 인라인 확장
 */
export default function ProblemAccordion({
  meta,
  posts,
  myVotes,
  currentUserId,
  isAdmin,
  sessions,
  onPostChanged,
  onRefreshNeeded,
  anchorId,
  open: controlledOpen,
  onToggle,
  defaultOpen = false,
  expandedPostId = null,
}: ProblemAccordionProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const toggleOpen = () => {
    if (isControlled) onToggle?.();
    else setUncontrolledOpen((v) => !v);
  };
  const [editingMeta, setEditingMeta] = useState(false);

  const sorted = useMemo(() => sortRestorationPosts(posts), [posts]);
  const topNet = useMemo(() => {
    if (sorted.length === 0) return 0;
    const top = sorted[0];
    return (top.up_count ?? 0) - (top.down_count ?? 0);
  }, [sorted]);

  return (
    <section
      id={anchorId}
      className="navbar-scroll-mt-20 rounded-md border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900/30 overflow-hidden"
    >
      <button
        type="button"
        onClick={toggleOpen}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-neutral-800/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-500"
      >
        {open ? (
          <ChevronDown className="w-4 h-4 shrink-0 text-gray-500" aria-hidden="true" />
        ) : (
          <ChevronRight className="w-4 h-4 shrink-0 text-gray-500" aria-hidden="true" />
        )}
        <span className="font-semibold text-sm w-12 shrink-0">
          {meta.problem_number}번
        </span>
        {meta.category && (
          <span className="shrink-0 rounded-full bg-gray-100 dark:bg-neutral-800 text-xs px-2 py-0.5">
            {meta.category}
          </span>
        )}
        <span className="flex-1 text-sm text-gray-600 dark:text-neutral-400 truncate">
          {meta.summary || "요약 없음"}
        </span>
        <span className="shrink-0 text-xs text-gray-500 dark:text-neutral-400">
          복원 {sorted.length} · 최고 공감수 {topNet >= 0 ? "+" : ""}
          {topNet}
        </span>
      </button>

      {open && (
        <div className="border-t border-gray-200 dark:border-neutral-700 bg-gray-50/50 dark:bg-neutral-900/20 p-4 flex flex-col gap-3">
          {isAdmin && (
            <>
              {editingMeta ? (
                <ProblemMetaAdminPanel
                  meta={meta}
                  onSaved={() => {
                    setEditingMeta(false);
                    onRefreshNeeded();
                  }}
                  onCancel={() => setEditingMeta(false)}
                />
              ) : (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setEditingMeta(true)}
                    className="inline-flex items-center gap-1 rounded-md border border-blue-200 dark:border-blue-800 px-2 py-1 text-xs text-blue-600 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40"
                  >
                    <Pencil className="w-3 h-3" />
                    메타 편집
                  </button>
                </div>
              )}
            </>
          )}

          {sorted.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500 dark:text-neutral-400">
              <FileQuestion className="w-4 h-4" />
              아직 이 문제에 복원된 게시물이 없습니다.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {sorted.map((post, idx) => (
                <PostCard
                  key={post.id}
                  post={post}
                  myVote={myVotes[post.id] ?? null}
                  currentUserId={currentUserId}
                  isAdmin={isAdmin}
                  sessions={sessions}
                  initialExpanded={idx === 0 || post.id === expandedPostId}
                  alwaysExpanded={idx === 0}
                  onPostChanged={onPostChanged}
                  onRefreshNeeded={onRefreshNeeded}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
